import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, ClipboardList, Users, Settings as SettingsIcon,
  Briefcase, Clock4, LogOut, Building2, FolderLock, ShieldCheck, Tags,
  History, Cloud, UserCog, Mail, Inbox, UserPlus, BadgeCheck, Send, Shield,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import StorageHealthDot from '../../components/StorageHealthDot';
import InviteModal from '../../components/InviteModal';
import { toast } from 'sonner';

// roles: 'admin' | 'specialist' (covers tax_specialist + advisor)
const LINKS = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true, roles: ['admin', 'specialist'] },
  { to: '/admin/documents', icon: FolderLock, label: 'Document Repository', roles: ['admin', 'specialist'] },
  { to: '/admin/calendar', icon: Calendar, label: 'Calendar', roles: ['admin', 'specialist'] },
  { to: '/admin/appointments', icon: ClipboardList, label: 'Appointments', roles: ['admin', 'specialist'] },
  { to: '/admin/clients', icon: Users, label: 'Clients', roles: ['admin', 'specialist'] },
  { to: '/admin/tax-specialists', icon: UserCog, label: 'Tax Specialists', roles: ['admin'] },
  { to: '/admin/specialist-applications', icon: BadgeCheck, label: 'Specialist Applications', roles: ['admin'] },
  { to: '/admin/assignments', icon: ShieldCheck, label: 'Specialist Assignments', roles: ['admin', 'specialist'] },
  { to: '/admin/member-requests', icon: Send, label: 'Member Requests', roles: ['specialist'] },
  { to: '/admin/document-categories', icon: Tags, label: 'Doc Categories', roles: ['admin'], disabledForSpecialist: 'Administrator permissions required' },
  { to: '/admin/workflow-stages', icon: ClipboardList, label: 'Workflow Stages', roles: ['admin'], disabledForSpecialist: 'Administrator permissions required' },
  { to: '/admin/dropbox', icon: Cloud, label: 'Dropbox', accessory: 'storage-health', roles: ['admin'], disabledForSpecialist: 'Dropbox managed by Administrator' },
  { to: '/admin/emails', icon: Mail, label: 'Emails', roles: ['admin'], disabledForSpecialist: 'Email templates managed by Administrator' },
  { to: '/admin/leads', icon: Inbox, label: 'Leads', roles: ['admin'], disabledForSpecialist: 'Leads inbox managed by Administrator' },
  { to: '/admin/audit-log', icon: History, label: 'Audit Log', roles: ['admin', 'specialist'] },
  { to: '/admin/types', icon: Briefcase, label: 'Service Types', roles: ['admin', 'specialist'] },
  { to: '/admin/availability', icon: Clock4, label: 'Availability', roles: ['admin', 'specialist'] },
  { to: '/admin/me', icon: UserCog, label: 'My Profile', roles: ['specialist'] },
  { to: '/admin/turnstile', icon: Shield, label: 'Bot Protection', roles: ['admin'] },
  { to: '/admin/settings', icon: SettingsIcon, label: 'Settings', roles: ['admin'] },
];

function classify(user) {
  if (user?.role === 'admin') return 'admin';
  if (user?.role === 'tax_specialist' || user?.role === 'advisor') return 'specialist';
  return null;
}

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = () => { logout(); nav('/admin/login'); };
  const roleKey = classify(user);
  const [inviteOpen, setInviteOpen] = useState(null);  // 'member' | 'specialist' | null
  const avatar = user?.profile_picture_url
    ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture_url}`
    : null;

  return (
    <div className="min-h-screen bg-[#0c0a08] text-white flex">
      <aside className="hidden lg:flex flex-col w-[252px] bg-[#14110f] border-r border-white/5 sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-2.5 px-6 h-[78px] border-b border-white/5">
          <div>
            <p className="font-extrabold text-[18px] leading-none text-white">WYSE<span className="text-[#f5b300]">Tribe</span></p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1.5">
              {roleKey === 'specialist' ? 'Specialist' : 'Admin'}
            </p>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-auto">
          {LINKS.map((link) => {
            const { to, icon: Icon, label, end, accessory, roles, disabledForSpecialist } = link;
            const allowed = roleKey && roles.includes(roleKey);
            const greyedOut = roleKey === 'specialist' && disabledForSpecialist;
            if (!allowed && !greyedOut) return null;

            if (greyedOut) {
              return (
                <div
                  key={to}
                  title={disabledForSpecialist}
                  data-testid={`nav-disabled-${to.split('/').pop()}`}
                  className="flex items-center gap-3 px-3.5 py-2.5 rounded-md text-[13.5px] font-medium text-white/30 cursor-not-allowed select-none"
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{label}</span>
                  {accessory === 'storage-health' && <StorageHealthDot isAdmin={true} />}
                </div>
              );
            }

            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-md text-[13.5px] font-medium transition-colors ${
                    isActive ? 'bg-[#f5b300] text-[#14110f]' : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{label}</span>
                {accessory === 'storage-health' && <StorageHealthDot isAdmin={user?.role === 'admin'} />}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2">
            {avatar ? (
              <img src={avatar} alt={user?.name} className="w-9 h-9 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#f5b300]/15 flex items-center justify-center text-[#f5b300] font-bold text-[14px]">
                {(user?.name || 'A').slice(0, 1)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold truncate">{user?.name}</p>
              <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
            </div>
            <button onClick={onLogout} title="Sign out" className="w-9 h-9 rounded-md hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 bg-[#f6f4ef] text-[#14110f]">
        <div className="h-[78px] bg-white border-b border-neutral-100 flex items-center justify-between px-6 lg:px-10 sticky top-0 z-30">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#14110f] leading-none">{title}</h1>
            {subtitle && <p className="text-[12.5px] text-neutral-500 mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {roleKey && (
              <>
                <button data-testid="invite-member-btn"
                        onClick={() => { toast.dismiss(); setInviteOpen('member'); }}
                        className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-[12.5px] font-semibold text-neutral-700 hover:border-[#f5b300] hover:text-[#c89200]">
                  <UserPlus className="w-3.5 h-3.5" />Invite member
                </button>
                <button data-testid="invite-specialist-btn"
                        onClick={() => { toast.dismiss(); setInviteOpen('specialist'); }}
                        className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#14110f] text-white text-[12.5px] font-semibold hover:bg-black">
                  <BadgeCheck className="w-3.5 h-3.5" />Invite specialist
                </button>
              </>
            )}
            {actions}
            <Link to="/" className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#c89200]">
              <Building2 className="w-4 h-4" /> View site
            </Link>
          </div>
        </div>
        <div className="p-6 lg:p-10">{children}</div>
      </main>
      <InviteModal kind={inviteOpen || 'member'} open={!!inviteOpen} onClose={() => setInviteOpen(null)} />
    </div>
  );
}
